'use client';

import { useState, useRef, useEffect } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { LabelDefault, MetaDefault } from '@terra/ui-design-system';
import { BotIcon, TerminalIcon, Cancel01Icon } from '@terra/ui-icons';
import { closeTerminalTabProcess } from './close-terminal-tab-process';

type TerminalTabItem = {
  id: string;
  title: string;
  sessionId: string;
  shell?: string;
  ptyId?: string;
  isActive: boolean;
};
type TerminalSidebarProps = {
  terminalTabs: TerminalTabItem[];
  activeTerminalTab?: {
    id: string;
  };
  setActiveTerminalTab: (id: string) => void;
  removeTerminalTab: (id: string) => void;
};
const EXPANDED_WIDTH_THRESHOLD = 56;

export function TerminalSidebar({
  terminalTabs,
  setActiveTerminalTab,
  removeTerminalTab,
}: TerminalSidebarProps) {
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsExpanded(entry.contentRect.width >= EXPANDED_WIDTH_THRESHOLD);
      }
    });

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <VStack
      ref={containerRef}
      gap={0}
      h="full"
      w="full"
      bg="bg.canvas"
      overflowY="auto"
      overflowX="hidden"
      alignItems="stretch"
      py="1"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--colors-border-subtle)',
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'var(--colors-border-default)',
        },
      }}
    >
      {terminalTabs.map((tab, index) => {
        const isHovered = hoveredTabId === tab.id;
        const tabLabel = tab.ptyId
          ? tab.title
          : tab.shell || tab.title || 'zsh';

        return (
          <Box
            key={tab.id}
            position="relative"
            h="26px"
            w="full"
            bg={tab.isActive ? 'bg.subtle' : 'transparent'}
            cursor="pointer"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent={isExpanded ? 'flex-start' : 'center'}
            px={isExpanded ? '2' : '0'}
            gap="2"
            _hover={{
              bg: 'bg.subtle',
            }}
            onClick={() => setActiveTerminalTab(tab.id)}
            onMouseEnter={() => setHoveredTabId(tab.id)}
            onMouseLeave={() => setHoveredTabId(null)}
          >
            <Box
              color={tab.isActive ? 'fg.default' : 'fg.muted'}
              display="flex"
              alignItems="center"
              flexShrink={0}
            >
              {tab.ptyId ? <BotIcon size={14} /> : <TerminalIcon size={14} />}
            </Box>

            {isExpanded && (
              <>
                <LabelDefault
                  as="span"
                  color={tab.isActive ? 'fg.default' : 'fg.muted'}
                  truncate
                  flex="1"
                  title={tabLabel}
                >
                  {tabLabel}
                </LabelDefault>

                <MetaDefault color="fg.subtle" flexShrink={0}>
                  {index + 1}
                </MetaDefault>
              </>
            )}

            {isHovered && (
              <Box
                position="absolute"
                right="2px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="16px"
                h="16px"
                borderRadius="sm"
                flexShrink={0}
                bg="bg.subtle"
                color="fg.muted"
                _hover={{ bg: 'bg.muted', color: 'fg.default' }}
                onClick={(e) => {
                  e.stopPropagation();
                  closeTerminalTabProcess(tab).catch((error: unknown) => {
                    console.warn(
                      '[TerminalSidebar] Failed to kill session terminal',
                      error,
                    );
                  });
                  removeTerminalTab(tab.id);
                }}
              >
                <Cancel01Icon size={10} />
              </Box>
            )}
          </Box>
        );
      })}
    </VStack>
  );
}
